import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { jobCardsAPI, estimatesAPI, projectsAPI, configAPI, rateChartAPI, editLogsAPI, usersAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import EstimateForm from './EstimateForm';
import NotificationBell from './NotificationBell';
import appLogo from '../assets/logo.png';
import {
  LogOut,
  FileText,
  Calculator,
  Plus,
  Download,
  Receipt,
  Eye,
  Layers,
  Scissors,
  CircleDot,
  FileSpreadsheet,
  IndianRupee,
  Edit,
  Trash2,
  Save,
  X,
  Send,
  CheckCircle,
  Link2,
  History,
  AlertTriangle,
  Search,
  CornerUpLeft,
  UserPlus
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Units shown on bills, as on the factory's rate chart
const RATE_UNITS = ['NOS', 'MTR', 'SHEET', 'BOX'];

// Roles the accountant may add. Owner/Admin are intentionally absent and are also
// blocked on the server — this list only controls what the dropdown offers.
const ADDABLE_ROLES = [
  { value: 'machine_operator', label: 'Pressing Operator' },
  { value: 'cutting_operator', label: 'Cutting Operator' },
  { value: 'boring_operator', label: 'Boring Operator' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'accountant', label: 'Accountant' },
];

const AccountantDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('estimates');
  const [estimates, setEstimates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [config, setConfig] = useState(null);
  const [rateChart, setRateChart] = useState([]);
  const [rateChartTab, setRateChartTab] = useState('finishing');
  const [loading, setLoading] = useState(true);

  // Add-user dialog (add-only: no list, no edit, no delete, no password reveal)
  const emptyNewUser = { name: '', mobile: '', password: '', role: 'machine_operator' };
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [addingUser, setAddingUser] = useState(false);

  const handleAddUser = async () => {
    const name = newUser.name.trim();
    const mobile = newUser.mobile.trim();
    if (!name || !mobile || !newUser.password || !newUser.role) {
      toast({ title: "Missing details", description: "Name, mobile, password and role are all required.", variant: "destructive" });
      return;
    }
    setAddingUser(true);
    try {
      await usersAPI.createStaff({ name, mobile, password: newUser.password, role: newUser.role });
      const roleLabel = ADDABLE_ROLES.find(r => r.value === newUser.role)?.label || newUser.role;
      toast({ title: "User added", description: `${name} (${roleLabel}) can now log in with mobile ${mobile}.` });
      setNewUser(emptyNewUser);
      setShowAddUser(false);
    } catch (error) {
      toast({ title: "Couldn't add user", description: error.response?.data?.detail || "Failed to add user", variant: "destructive" });
    } finally {
      setAddingUser(false);
    }
  };

  // Toggle this to enable/disable accountant's ability to edit estimates
  // Rate chart editing remains always enabled
  // Set to true when accountant needs to edit estimate lines directly
  const ESTIMATE_EDIT_ENABLED = false;
  
  // Dialog states
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingEstimate, setViewingEstimate] = useState(null);
  
  // Rate Chart states
  const [editingRateId, setEditingRateId] = useState(null);
  const [editRateData, setEditRateData] = useState({ description: '', rate: 0 });
  const [showAddRateDialog, setShowAddRateDialog] = useState(false);
  const [newRateData, setNewRateData] = useState({ description: '', rate: 0, category: 'finishing' });
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Edit log states
  const [editLogs, setEditLogs] = useState([]);
  const [showEditLogDialog, setShowEditLogDialog] = useState(false);
  const [selectedEstimateLogs, setSelectedEstimateLogs] = useState([]);
  
  // Edit estimate state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const editEstimateFormRef = useRef();

  // Payment dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentEstimate, setPaymentEstimate] = useState(null);
  const [partialAmount, setPartialAmount] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountReasonSkipped, setDiscountReasonSkipped] = useState(false);
  const [roundOffAmount, setRoundOffAmount] = useState('');

  // Return-to-coordinator dialog state
  const [returnEstimate, setReturnEstimate] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returning, setReturning] = useState(false);

  // Estimates list filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [coordinatorFilter, setCoordinatorFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [estimatesData, projectsData, configData, rateChartData, editLogsData] = await Promise.all([
        estimatesAPI.getAll(),
        projectsAPI.getAll(),
        configAPI.get(),
        rateChartAPI.getAll(),
        editLogsAPI.getAll().catch(() => [])
      ]);
      const projectMap = {};
      projectsData.forEach(p => { projectMap[p.id] = p; });
      setEstimates(estimatesData.map(est => ({
        ...est,
        coordinator_name: projectMap[est.project_id]?.created_by_name || null,
      })));
      setProjects(projectsData);
      setConfig(configData);
      setRateChart(rateChartData);
      setEditLogs(editLogsData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (estimate) => {
    setEditingEstimate(estimate);
    setShowEditDialog(true);
  };

  const handleUpdateEstimate = async () => {
    if (!editEstimateFormRef.current || !editingEstimate) return;
    
    const formData = editEstimateFormRef.current.getData();
    
    try {
      await estimatesAPI.update(editingEstimate.id, {
        customer_name: formData.name,
        reference: formData.reference,
        estimate_lines: formData.estimate_lines.filter(l => l.particular).map(l => ({
          particular: l.particular,
          qty: parseInt(l.qty) || 0,
          unit: l.unit || '',
          rate: parseFloat(l.rate) || 0
        })),
        is_invoice: editingEstimate.is_invoice
      });
      
      toast({ title: "Success", description: "Estimate updated successfully" });
      setShowEditDialog(false);
      setEditingEstimate(null);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update estimate", variant: "destructive" });
    }
  };

  const handleSendToClient = async (est) => {
    try {
      await estimatesAPI.sendToClient(est.id);
      toast({ title: "Success", description: "Invoice marked as sent to client" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const handleToggleSettled = async (est, value) => {
    try {
      await estimatesAPI.setSettled(est.id, value);
      // Optimistic local update so the checkbox/badge respond instantly
      setEstimates(prev => prev.map(e => e.id === est.id ? { ...e, settled: value } : e));
      toast({ title: value ? "Marked settled" : "Settled removed", description: value ? "Sent for final reconciliation." : "" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update settled flag", variant: "destructive" });
      fetchData();
    }
  };

  // Only a plain draft estimate (not sent, not paid) can be bounced back to the coordinator
  const canReturn = (est) =>
    !!est.job_card_id && !est.sent_to_client && !est.payment_received && est.payment_status !== 'partial';

  const handleReturnToCoordinator = async () => {
    if (!returnEstimate || !returnReason.trim()) return;
    setReturning(true);
    try {
      const res = await jobCardsAPI.returnToCoordinator(returnEstimate.job_card_id, returnReason.trim());
      toast({ title: "Returned to coordinator", description: res?.message || "The coordinator can now make changes." });
      setReturnEstimate(null);
      setReturnReason('');
      fetchData();
    } catch (error) {
      toast({
        title: "Could not return",
        description: error.response?.data?.detail || "Failed to return job card",
        variant: "destructive"
      });
    } finally {
      setReturning(false);
    }
  };

  const openPaymentDialog = (est) => {
    setPaymentEstimate(est);
    setPartialAmount('');
    setDiscountAmount(est.discount ? String(est.discount) : '');
    setDiscountReason(est.discount_reason || '');
    setDiscountReasonSkipped(false);
    setRoundOffAmount(est.round_off ? String(est.round_off) : '');
    setShowPaymentDialog(true);
  };

  const handleFullPayment = async () => {
    const disc = parseFloat(discountAmount) || 0;
    const roff = parseFloat(roundOffAmount) || 0;
    try {
      const res = await estimatesAPI.markPaymentReceived(paymentEstimate.id, null, disc, roff, discountReason);
      toast({ title: "Success", description: res.message || "Full payment recorded! Job card closed." });
      setShowPaymentDialog(false);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to record payment", variant: "destructive" });
    }
  };

  const handlePartialPayment = async () => {
    const amt = parseFloat(partialAmount);
    const disc = parseFloat(discountAmount) || 0;
    const roff = parseFloat(roundOffAmount) || 0;
    if (!amt || amt <= 0) {
      toast({ title: "Error", description: "Enter a valid amount", variant: "destructive" });
      return;
    }
    try {
      const res = await estimatesAPI.markPaymentReceived(paymentEstimate.id, amt, disc, roff, discountReason);
      toast({ title: "Success", description: res.message || "Partial payment recorded." });
      setShowPaymentDialog(false);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to record payment", variant: "destructive" });
    }
  };

  const handleViewEditLogs = async (est) => {
    try {
      const logs = await editLogsAPI.getByEstimate(est.id);
      setSelectedEstimateLogs(logs);
      setShowEditLogDialog(true);
    } catch (error) {
      setSelectedEstimateLogs([]);
      setShowEditLogDialog(true);
    }
  };


  // Export using API endpoints
  const handleExportEstimatePDF = async (estimateId, estimateNumber) => {
    try {
      const blob = await estimatesAPI.exportPDF(estimateId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estimate_${estimateNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "PDF downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export PDF", variant: "destructive" });
    }
  };

  const handleExportEstimateExcel = async (estimateId, estimateNumber) => {
    try {
      const blob = await estimatesAPI.exportExcel(estimateId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estimate_${estimateNumber}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "Success", description: "Excel downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export Excel", variant: "destructive" });
    }
  };

  const handleShareWhatsApp = async (estimate) => {
    try {
      toast({ title: "Generating PDF...", description: "Please wait" });
      const result = await estimatesAPI.shareWhatsApp(estimate.id);
      
      if (result.whatsapp_url) {
        // Open WhatsApp with the message
        window.open(result.whatsapp_url, '_blank');
        toast({ 
          title: "WhatsApp Opened", 
          description: "PDF link included in message. Send to share!" 
        });
      } else {
        toast({ title: "Error", description: "Could not generate WhatsApp link", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to share via WhatsApp", variant: "destructive" });
    }
  };

  const handleCopyLink = async (estimate) => {
    try {
      toast({ title: "Generating PDF...", description: "Please wait" });
      const result = await estimatesAPI.shareWhatsApp(estimate.id);
      
      if (result.download_url) {
        const fullUrl = `${window.location.origin}${result.download_url}`;
        
        // Try clipboard API first, fallback to execCommand
        try {
          await navigator.clipboard.writeText(fullUrl);
        } catch (clipboardError) {
          // Fallback for older browsers or when clipboard API fails
          const textArea = document.createElement('textarea');
          textArea.value = fullUrl;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        
        toast({ 
          title: "Link Copied!", 
          description: "PDF download link copied to clipboard" 
        });
      } else {
        toast({ title: "Error", description: "Could not generate download link", variant: "destructive" });
      }
    } catch (error) {
      console.error('Copy link error:', error);
      toast({ title: "Error", description: "Failed to copy link", variant: "destructive" });
    }
  };

  const exportToPDF = (estimate) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${estimate.is_invoice ? 'Invoice' : 'Estimate'} - ${estimate.estimate_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; padding: 30px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #1e40af; }
            .header h1 { font-size: 22px; color: #1e40af; margin-bottom: 5px; }
            .header p { font-size: 10px; color: #666; }
            .title { text-align: center; background: #e5e7eb; padding: 8px; font-weight: bold; font-size: 16px; margin-bottom: 20px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #000; margin-bottom: 20px; }
            .info-row { display: flex; border-bottom: 1px solid #ccc; }
            .info-row:last-child { border-bottom: none; }
            .info-label { background: #f3f4f6; padding: 8px; width: 100px; font-weight: bold; border-right: 1px solid #ccc; }
            .info-value { padding: 8px; flex: 1; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th { background: #e5e7eb; padding: 10px 8px; text-align: left; border: 1px solid #000; font-weight: bold; }
            td { padding: 8px; border: 1px solid #000; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .total-section { background: #fef9c3; padding: 15px; border: 2px solid #000; }
            .total-row { display: flex; justify-content: flex-end; padding: 5px 0; }
            .total-label { width: 150px; font-weight: bold; }
            .total-value { width: 120px; text-align: right; font-size: 14px; }
            .grand-total { font-size: 18px; font-weight: bold; color: #15803d; border-top: 2px solid #000; padding-top: 10px; margin-top: 10px; }
            @media print { body { padding: 15px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INTERIOR STUDIO</h1>
            <p>kadavu road, Kunnumparam, Muttar</p>
          </div>
          
          <div class="title">ESTIMATE</div>
          
          <div class="info-grid">
            <div>
              <div class="info-row">
                <span class="info-label">JOB CARD</span>
                <span class="info-value">${estimate.job_card_number || '-'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">NAME</span>
                <span class="info-value">${estimate.customer_name}</span>
              </div>
            </div>
            <div>
              <div class="info-row">
                <span class="info-label">DATE</span>
                <span class="info-value">${new Date(estimate.date).toLocaleDateString('en-IN')}</span>
              </div>
              <div class="info-row">
                <span class="info-label">REF</span>
                <span class="info-value">${estimate.reference || '-'}</span>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 50px;" class="text-center">SL NO</th>
                <th>PARTICULARS</th>
                <th style="width: 70px;" class="text-right">QTY</th>
                <th style="width: 60px;" class="text-center">UNIT</th>
                <th style="width: 90px;" class="text-right">RATE</th>
                <th style="width: 100px;" class="text-right">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              ${estimate.estimate_lines.map(line => `
                <tr>
                  <td class="text-center">${line.sl_no}</td>
                  <td>${String(line.particular || "").toUpperCase()}</td>
                  <td class="text-right">${line.qty}</td>
                  <td class="text-center">${line.unit || ''}</td>
                  <td class="text-right">${line.rate.toFixed(2)}</td>
                  <td class="text-right">${line.total.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row grand-total">
              <span class="total-label">TOTAL AMOUNT</span>
              <span class="total-value">₹ ${(estimate.total_amount || 0).toFixed(2)}</span>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Rate Chart Functions
  const handleEditRate = (item) => {
    setEditingRateId(item.id);
    setEditRateData({ description: item.description, rate: item.rate, unit: item.unit || '' });
  };

  const handleSaveRate = async (itemId) => {
    try {
      await rateChartAPI.update(itemId, editRateData);
      toast({ title: "Success", description: "Rate updated successfully" });
      setEditingRateId(null);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update rate", variant: "destructive" });
    }
  };

  const handleCancelEdit = () => {
    setEditingRateId(null);
    setEditRateData({ description: '', rate: 0 });
  };

  const handleDeleteRate = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this rate item?')) return;
    try {
      await rateChartAPI.delete(itemId);
      toast({ title: "Success", description: "Rate deleted successfully" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete rate", variant: "destructive" });
    }
  };

  const handleAddRate = async () => {
    if (!newRateData.description.trim()) {
      toast({ title: "Error", description: "Description is required", variant: "destructive" });
      return;
    }
    try {
      await rateChartAPI.create({ ...newRateData, category: rateChartTab });
      toast({ title: "Success", description: "Rate added successfully" });
      setShowAddRateDialog(false);
      setNewRateData({ description: '', rate: 0, category: 'finishing' });
      setShowSuggestions(false);
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add rate", variant: "destructive" });
    }
  };

  const fetchItemSuggestions = async () => {
    try {
      const data = await jobCardsAPI.getItemDescriptions();
      // Filter out items that are already in the rate chart
      const existingDescs = rateChart.map(r => r.description.toUpperCase());
      const filtered = data.filter(d => !existingDescs.includes(d.toUpperCase()));
      setItemSuggestions(filtered);
    } catch (err) {
      setItemSuggestions([]);
    }
  };

  // Derive a single status key per estimate for filtering
  const getEstStatus = (est) => {
    if (est.payment_received) return 'paid';
    if (est.payment_status === 'partial') return 'partial';
    if (est.sent_to_client) return 'sent';
    if (est.is_invoice) return 'invoice';
    return 'estimate';
  };

  // Unique coordinator names present in the estimates
  const coordinatorOptions = Array.from(
    new Set(estimates.map(e => e.coordinator_name).filter(Boolean))
  ).sort();

  const filteredEstimates = estimates.filter(est => {
    if (statusFilter === 'settled') {
      if (!est.settled) return false;
    } else if (statusFilter !== 'all' && getEstStatus(est) !== statusFilter) {
      return false;
    }
    if (coordinatorFilter !== 'all' && est.coordinator_name !== coordinatorFilter) return false;
    if (clientSearch.trim()) {
      const q = clientSearch.trim().toLowerCase();
      if (!(est.customer_name || '').toLowerCase().includes(q) &&
          !(est.reference || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const statusChips = [
    { key: 'all', label: 'All' },
    { key: 'estimate', label: 'Estimate' },
    { key: 'sent', label: 'Sent to Client' },
    { key: 'partial', label: 'Partial' },
    { key: 'paid', label: 'Paid' },
    { key: 'invoice', label: 'Invoice' },
    { key: 'settled', label: 'Settled' },
  ];

  // Live payment dialog figures (discount + round off both reduce the amount owed)
  const payDisc = parseFloat(discountAmount) || 0;
  const payRoff = parseFloat(roundOffAmount) || 0;
  const payTotal = paymentEstimate ? (paymentEstimate.total_amount || 0) : 0;
  const payPrevPaid = paymentEstimate ? (paymentEstimate.amount_paid || 0) : 0;
  const payEffectiveTotal = Math.max(0, payTotal - payDisc - payRoff);
  const payBalance = Math.max(0, payEffectiveTotal - payPrevPaid);
  // A discount prompts for a reason. Recording is blocked until they either type
  // one or press Skip (a Skip = a plain discount, no reason, no flag).
  const needDiscountReason = payDisc > 0 && !discountReason.trim() && !discountReasonSkipped;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
              <img src={appLogo} alt="Interior Job Work" className="w-[60px] h-[60px] object-cover" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Interior Job Work</h1>
              <p className="text-xs text-gray-500">Billing & Invoices</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden sm:block">{user?.name}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setNewUser(emptyNewUser); setShowAddUser(true); }}
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-3 lg:w-[450px]">
            <TabsTrigger value="estimates" className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              <span>Estimates</span>
            </TabsTrigger>
            <TabsTrigger value="edit-log" className="flex items-center gap-2" data-testid="edit-log-tab">
              <History className="w-4 h-4" />
              <span>Edit Log</span>
            </TabsTrigger>
            <TabsTrigger value="rates" className="flex items-center gap-2" data-testid="rate-chart-tab">
              <IndianRupee className="w-4 h-4" />
              <span>Rate Chart</span>
            </TabsTrigger>
          </TabsList>

          {/* Estimates List */}
          <TabsContent value="estimates" className="space-y-4">
            <h2 className="text-lg font-semibold">All Estimates & Invoices</h2>

            {/* Filters & search */}
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by client or reference..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-9"
                    data-testid="client-search"
                  />
                </div>
                <Select value={coordinatorFilter} onValueChange={setCoordinatorFilter}>
                  <SelectTrigger className="sm:w-56" data-testid="coordinator-filter">
                    <SelectValue placeholder="All Coordinators" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Coordinators</SelectItem>
                    {coordinatorOptions.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 flex-wrap">
                {statusChips.map(chip => (
                  <Button
                    key={chip.key}
                    size="sm"
                    variant={statusFilter === chip.key ? 'default' : 'outline'}
                    className={statusFilter === chip.key ? '' : 'text-gray-600'}
                    onClick={() => setStatusFilter(chip.key)}
                    data-testid={`status-filter-${chip.key}`}
                  >
                    {chip.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Showing {filteredEstimates.length} of {estimates.length}
                {(statusFilter !== 'all' || coordinatorFilter !== 'all' || clientSearch.trim()) && (
                  <button className="ml-2 text-blue-600 hover:underline" onClick={() => { setStatusFilter('all'); setCoordinatorFilter('all'); setClientSearch(''); }}>
                    Clear filters
                  </button>
                )}
              </p>
            </div>

            {filteredEstimates.length > 0 ? (
              <div className="grid gap-4">
                {filteredEstimates.map(est => (
                  <Card key={est.id} className={`${est.payment_received ? 'opacity-75' : ''} ${est.payment_status === 'partial' ? 'border-l-4 border-l-yellow-400' : est.edit_count > 0 ? 'border-l-4 border-l-amber-400' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold">{est.estimate_number}</h3>
                            {est.payment_received ? (
                              <Badge className="bg-green-500 text-white">Paid</Badge>
                            ) : est.payment_status === 'partial' ? (
                              <Badge className="bg-yellow-500 text-white">Partial</Badge>
                            ) : est.sent_to_client ? (
                              <Badge className="bg-purple-100 text-purple-800">Sent to Client</Badge>
                            ) : est.is_invoice ? (
                              <Badge className="bg-blue-100 text-blue-800">Invoice</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-800">Estimate</Badge>
                            )}
                            {est.settled && (
                              <Badge className="bg-teal-600 text-white"><CheckCircle className="w-3 h-3 mr-1" /> Settled</Badge>
                            )}
                            {est.edit_count > 0 && (
                              <Badge 
                                className="bg-amber-100 text-amber-800 cursor-pointer hover:bg-amber-200" 
                                onClick={() => handleViewEditLogs(est)}
                                data-testid={`edited-badge-${est.estimate_number}`}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                Edited ({est.edit_count}x)
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{est.customer_name}</p>
                          {est.coordinator_name && (
                            <p className="text-xs text-blue-600">Coordinator: {est.coordinator_name}</p>
                          )}
                          {est.reference && (
                            <p className="text-xs text-gray-500">Ref: {est.reference}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            {new Date(est.date).toLocaleDateString('en-IN')}
                            {est.job_card_number && ` | JC: ${est.job_card_number}`}
                          </p>
                          {est.sent_to_client_at && (
                            <p className="text-xs text-purple-600 mt-0.5">
                              Sent: {new Date(est.sent_to_client_at).toLocaleDateString('en-IN')}
                            </p>
                          )}
                          {est.last_edited_at && (
                            <p className="text-xs text-amber-600 mt-0.5">
                              Last edited: {new Date(est.last_edited_at).toLocaleDateString('en-IN')} by {est.last_edited_by || 'Unknown'}
                            </p>
                          )}
                          {est.payment_status === 'partial' && (
                            <p className="text-xs text-yellow-600 mt-0.5 font-medium">
                              Paid: ₹{(est.amount_paid || 0).toFixed(2)} / ₹{((est.total_amount || 0) - (est.discount || 0) - (est.round_off || 0)).toFixed(2)} | Balance: ₹{((est.total_amount || 0) - (est.discount || 0) - (est.round_off || 0) - (est.amount_paid || 0)).toFixed(2)}
                            </p>
                          )}
                          {(est.round_off || 0) > 0 && (
                            <p className="text-xs text-blue-600 mt-0.5" title="Shown on client invoice">
                              Round Off: ₹{(est.round_off || 0).toFixed(2)}
                            </p>
                          )}
                          {(est.discount || 0) > 0 && (
                            <p className="text-xs text-orange-600 mt-0.5" title="Internal only — not shown on client invoice">
                              Discount (internal): ₹{(est.discount || 0).toFixed(2)}
                              {est.discount_reason ? <span className="text-gray-500"> — “{est.discount_reason}”</span> : null}
                            </p>
                          )}
                          {est.payment_received && est.payment_date && (
                            <p className="text-xs text-green-600 mt-0.5">
                              Payment: {new Date(est.payment_date).toLocaleDateString('en-IN')}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xl font-bold text-green-600">
                            ₹ {(est.total_amount || 0).toFixed(2)}
                          </p>
                          
                          {/* Action buttons row 1: View, Edit, PDF, Excel, WhatsApp, Copy Link */}
                          <div className="flex gap-2 mt-2 justify-end flex-wrap items-center">
                            <label className="flex items-center gap-1.5 mr-auto text-sm text-gray-700 cursor-pointer select-none" title="Mark as settled — sent for final reconciliation with CA/audit">
                              <Checkbox checked={!!est.settled} onCheckedChange={(v) => handleToggleSettled(est, !!v)} data-testid={`settled-${est.estimate_number}`} />
                              <span className={est.settled ? 'text-teal-700 font-medium' : ''}>Settled</span>
                            </label>
                            <Button variant="outline" size="sm" onClick={() => { setViewingEstimate(est); setShowViewDialog(true); }} data-testid={`view-estimate-${est.estimate_number}`}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                            {ESTIMATE_EDIT_ENABLED && (
                            <Button variant="outline" size="sm" onClick={() => openEditDialog(est)} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid={`edit-estimate-${est.estimate_number}`}>
                              <Edit className="w-4 h-4 mr-1" /> Edit
                            </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleExportEstimatePDF(est.id, est.estimate_number)} title="Export PDF">
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleExportEstimateExcel(est.id, est.estimate_number)} title="Export Excel">
                              <FileSpreadsheet className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-green-600 border-green-300 hover:bg-green-50" 
                              onClick={() => handleShareWhatsApp(est)} 
                              title="Send PDF via WhatsApp"
                              data-testid={`whatsapp-estimate-${est.estimate_number}`}
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-purple-600 border-purple-300 hover:bg-purple-50" 
                              onClick={() => handleCopyLink(est)} 
                              title="Copy PDF Link"
                              data-testid={`copy-link-estimate-${est.estimate_number}`}
                            >
                              <Link2 className="w-4 h-4" />
                            </Button>
                            {canReturn(est) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={() => { setReturnEstimate(est); setReturnReason(''); }}
                                title="Return this job card to the coordinator for changes"
                                data-testid={`return-estimate-${est.estimate_number}`}
                              >
                                <CornerUpLeft className="w-4 h-4 mr-1" /> Return
                              </Button>
                            )}
                          </div>

                          {/* Action buttons row 2: Send & Payment */}
                          {!est.payment_received && (
                            <div className="flex gap-2 mt-2 justify-end flex-wrap">
                              {!est.sent_to_client && est.payment_status !== 'partial' && (
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  onClick={() => handleSendToClient(est)}
                                  data-testid={`mark-sent-${est.estimate_number}`}
                                >
                                  <Send className="w-4 h-4 mr-1" /> Mark as Sent
                                </Button>
                              )}
                              {(est.sent_to_client || est.payment_status === 'partial') && (
                                <Button
                                  size="sm"
                                  className={est.payment_status === 'partial' ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                                  onClick={() => openPaymentDialog(est)}
                                  data-testid={`payment-received-${est.estimate_number}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" /> {est.payment_status === 'partial' ? 'Record Payment' : 'Mark Payment Received'}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">
                    {estimates.length === 0 ? 'No estimates created yet' : 'No estimates match the current filters'}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Edit Log Tab */}
          <TabsContent value="edit-log" className="space-y-4" data-testid="edit-log-content">
            <h2 className="text-lg font-semibold">Estimate Edit History</h2>
            
            {editLogs.length > 0 ? (
              <div className="space-y-3">
                {editLogs.map((log, idx) => (
                  <Card key={log.id || idx} className="border-l-4 border-l-amber-400">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-semibold text-sm">{log.estimate_number}</span>
                            {log.job_card_number && (
                              <Badge variant="outline" className="text-xs">JC: {log.job_card_number}</Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {log.customer_name}
                            </span>
                          </div>
                          <p className="text-xs text-amber-700 font-medium mb-2">
                            Edited by {log.edited_by_name} on {new Date(log.edited_at).toLocaleString('en-IN')}
                          </p>
                          <div className="space-y-1">
                            {log.changes?.map((change, ci) => (
                              <div key={ci} className="text-xs bg-gray-50 rounded px-2 py-1 flex items-start gap-2">
                                <span className="font-medium text-gray-700 flex-shrink-0">{change.field}:</span>
                                <div className="flex flex-wrap gap-1">
                                  {change.old_value && (
                                    <span className="line-through text-red-500">{change.old_value}</span>
                                  )}
                                  {change.old_value && change.new_value && <span className="text-gray-400">→</span>}
                                  {change.new_value && (
                                    <span className="text-green-600 font-medium">{change.new_value}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <History className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No edits have been made to any estimates yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Rate Chart Tab */}
          <TabsContent value="rates" className="space-y-4" data-testid="rate-chart-content">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Rate Charts</h2>
              <Button onClick={() => { setShowAddRateDialog(true); fetchItemSuggestions(); }} data-testid="add-rate-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add New Rate
              </Button>
            </div>

            {/* Rate Chart Sub-tabs */}
            <div className="flex gap-1 border-b">
              {[
                { key: 'normal_pressing', label: 'Normal Pressing' },
                { key: 'pvc_pressing', label: 'PVC & Acrylic Pressing' },
                { key: 'finishing', label: 'Finishing Works' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setRateChartTab(tab.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${rateChartTab === tab.key ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  data-testid={`rate-tab-${tab.key}`}
                >
                  {tab.label}
                  <span className="ml-1.5 text-xs text-gray-400">
                    ({rateChart.filter(r => (r.category || 'finishing') === tab.key).length})
                  </span>
                </button>
              ))}
            </div>
            
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-semibold border-b">#</th>
                      <th className="text-left p-3 font-semibold border-b">Description</th>
                      <th className="text-center p-3 font-semibold border-b w-28">Unit</th>
                      <th className="text-right p-3 font-semibold border-b">Rate (₹)</th>
                      <th className="text-center p-3 font-semibold border-b w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateChart.filter(r => (r.category || 'finishing') === rateChartTab).map((item, index) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`rate-row-${index}`}>
                        <td className="p-3 text-gray-500">{index + 1}</td>
                        <td className="p-3">
                          {editingRateId === item.id ? (
                            <Input
                              value={editRateData.description}
                              onChange={(e) => setEditRateData({ ...editRateData, description: e.target.value })}
                              className="h-8"
                              data-testid={`edit-description-${index}`}
                            />
                          ) : (
                            <span className="font-medium">{item.description}</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {editingRateId === item.id ? (
                            <select
                              value={editRateData.unit || ''}
                              onChange={(e) => setEditRateData({ ...editRateData, unit: e.target.value })}
                              className="h-8 px-2 border rounded text-sm"
                              data-testid={`edit-unit-${index}`}
                            >
                              <option value="">-</option>
                              {RATE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          ) : (
                            <span className="text-gray-700">{item.unit || '-'}</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {editingRateId === item.id ? (
                            <Input
                              type="number"
                              value={editRateData.rate}
                              onChange={(e) => setEditRateData({ ...editRateData, rate: parseFloat(e.target.value) || 0 })}
                              className="h-8 w-24 ml-auto text-right"
                              step="0.01"
                              data-testid={`edit-rate-${index}`}
                            />
                          ) : (
                            <span className="text-green-600 font-semibold">₹ {item.rate.toFixed(2)}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2">
                            {editingRateId === item.id ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleSaveRate(item.id)} title="Save" data-testid={`save-rate-${index}`}>
                                  <Save className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={handleCancelEdit} title="Cancel" data-testid={`cancel-edit-${index}`}>
                                  <X className="w-4 h-4 text-gray-500" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleEditRate(item)} title="Edit" data-testid={`edit-rate-btn-${index}`}>
                                  <Edit className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteRate(item.id)} title="Delete" data-testid={`delete-rate-btn-${index}`}>
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rateChart.filter(r => (r.category || 'finishing') === rateChartTab).length === 0 && (
                  <div className="p-8 text-center">
                    <IndianRupee className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No rates configured in this category</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add New Rate Dialog */}
      <Dialog open={showAddRateDialog} onOpenChange={(open) => { setShowAddRateDialog(open); if (open) fetchItemSuggestions(); setShowSuggestions(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Rate</DialogTitle>
            <DialogDescription>Select from existing items or type a new description</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2 relative">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={newRateData.description}
                onChange={(e) => { setNewRateData({ ...newRateData, description: e.target.value }); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Type or select from job card items..."
                data-testid="new-rate-description"
                autoComplete="off"
              />
              {showSuggestions && itemSuggestions.length > 0 && (
                <div className="absolute z-50 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto mt-1" data-testid="rate-suggestions-dropdown">
                  {itemSuggestions
                    .filter(s => !newRateData.description || s.toLowerCase().includes(newRateData.description.toLowerCase()))
                    .map((suggestion, idx) => (
                      <button
                        key={idx}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-50 last:border-0"
                        onClick={() => { setNewRateData({ ...newRateData, description: suggestion }); setShowSuggestions(false); }}
                        data-testid={`rate-suggestion-${idx}`}
                      >
                        {suggestion}
                      </button>
                    ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-rate-unit">Unit</Label>
              <select
                id="new-rate-unit"
                value={newRateData.unit || ''}
                onChange={(e) => setNewRateData({ ...newRateData, unit: e.target.value })}
                className="w-full h-9 px-2 border rounded-md text-sm"
                data-testid="new-rate-unit"
              >
                <option value="">-</option>
                {RATE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate">Rate (Rs)</Label>
              <Input
                id="rate"
                type="number"
                value={newRateData.rate}
                onChange={(e) => setNewRateData({ ...newRateData, rate: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                step="0.01"
                data-testid="new-rate-amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRateDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRate} data-testid="confirm-add-rate">Add Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Estimate Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {viewingEstimate?.is_invoice ? 'Invoice' : 'Estimate'} - {viewingEstimate?.estimate_number}
            </DialogTitle>
          </DialogHeader>
          {viewingEstimate && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">Job Card</p>
                  <p className="font-medium">{viewingEstimate.job_card_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Date</p>
                  <p className="font-medium">{new Date(viewingEstimate.date).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Customer</p>
                  <p className="font-medium">{viewingEstimate.customer_name}</p>
                </div>
                {viewingEstimate.created_by_name && (
                  <div>
                    <p className="text-xs text-gray-500">Coordinator</p>
                    <p className="font-medium text-blue-600">{viewingEstimate.created_by_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500">Reference</p>
                  <p className="font-medium">{viewingEstimate.reference || '-'}</p>
                </div>
              </div>

              <table className="w-full text-sm border">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-center w-12">SL NO</th>
                    <th className="border p-2 text-left">PARTICULARS</th>
                    <th className="border p-2 text-right w-16">QTY</th>
                    <th className="border p-2 text-center w-16">UNIT</th>
                    <th className="border p-2 text-right w-20">RATE</th>
                    <th className="border p-2 text-right w-24">TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingEstimate.estimate_lines.map(line => (
                    <tr key={line.id}>
                      <td className="border p-2 text-center">{line.sl_no}</td>
                      <td className="border p-2">{String(line.particular || '').toUpperCase()}</td>
                      <td className="border p-2 text-right">{line.qty}</td>
                      <td className="border p-2 text-center">{line.unit || ''}</td>
                      <td className="border p-2 text-right">{line.rate.toFixed(2)}</td>
                      <td className="border p-2 text-right font-medium">{line.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="bg-yellow-50 p-4 rounded border-2">
                <div className="flex justify-between py-2 text-lg font-bold">
                  <span>Total Amount:</span>
                  <span className="text-green-600">₹ {(viewingEstimate.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            <Button onClick={() => exportToPDF(viewingEstimate)}>
              <Download className="w-4 h-4 mr-2" /> Export PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Estimate Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) setEditingEstimate(null);
      }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-estimate-dialog">
          <DialogHeader>
            <DialogTitle>Edit Estimate - {editingEstimate?.estimate_number}</DialogTitle>
            <DialogDescription>Update the estimate details for {editingEstimate?.customer_name}</DialogDescription>
          </DialogHeader>
          
          {editingEstimate && (
            <EstimateForm 
              ref={editEstimateFormRef}
              initialData={{
                estimate_number: editingEstimate.estimate_number,
                job_card_number: editingEstimate.job_card_number || '',
                name: editingEstimate.customer_name,
                reference: editingEstimate.reference || '',
                date: editingEstimate.date ? new Date(editingEstimate.date).toISOString().split('T')[0] : '',
                estimate_lines: editingEstimate.estimate_lines?.map(l => ({
                  ...l,
                  qty: l.qty?.toString() || '',
                  rate: l.rate?.toString() || '',
                  isCustom: !config?.estimate_particulars?.includes(l.particular) && 
                    ![
                      'PRESSING WORK NORMAL', 'PRESSING WORK HOT', 'CUTTING WORK',
                      'EDGE BANDING (22*.8)', 'EDGE BANDING (22*2)', 'EDGE BANDING (45)',
                      'HANDLE PROFILE WORK', 'DOUBLE WORK M', 'DOUBLE WORK L', 'DOUBLE WORK XL',
                      'HINGES HOLE', 'CUTTING LIST PREPRATION', 'MINI FIX HOLE CUTTING',
                      'PACKING', 'HANDLING CHARGE', 'HANDLE PROFILE', 'ROTATING TV UNIT WORK',
                      'BORING WORK', 'GLASS DOOR CUTTING', 'MICA CUTTING', 'TRANSPORTATION',
                      'MEASUREMENT CHARGE'
                    ].includes(l.particular?.toUpperCase())
                })) || []
              }}
              config={config}
            />
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateEstimate} data-testid="save-estimate-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Log Dialog - for individual estimate */}
      <Dialog open={showEditLogDialog} onOpenChange={setShowEditLogDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              Edit History
            </DialogTitle>
            <DialogDescription>All changes made to this estimate</DialogDescription>
          </DialogHeader>
          
          {selectedEstimateLogs.length > 0 ? (
            <div className="space-y-3 py-2">
              {selectedEstimateLogs.map((log, idx) => (
                <div key={log.id || idx} className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-sm font-medium text-amber-700 mb-2">
                    {log.edited_by_name} — {new Date(log.edited_at).toLocaleString('en-IN')}
                  </p>
                  <div className="space-y-1">
                    {log.changes?.map((change, ci) => (
                      <div key={ci} className="text-xs flex items-start gap-2 bg-white rounded px-2 py-1">
                        <span className="font-medium text-gray-700 flex-shrink-0">{change.field}:</span>
                        <div className="flex flex-wrap gap-1">
                          {change.old_value && (
                            <span className="line-through text-red-500">{change.old_value}</span>
                          )}
                          {change.old_value && change.new_value && <span className="text-gray-400">→</span>}
                          {change.new_value && (
                            <span className="text-green-600 font-medium">{change.new_value}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <History className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p>No edits recorded for this estimate</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditLogDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {paymentEstimate && (
                <>
                  {paymentEstimate.estimate_number} — {paymentEstimate.customer_name}
                  <br />
                  Total: ₹{payTotal.toFixed(2)}
                  {(payDisc > 0 || payRoff > 0) && <> | Net payable: ₹{payEffectiveTotal.toFixed(2)}</>}
                  {payPrevPaid > 0 && <> | Already Paid: ₹{payPrevPaid.toFixed(2)} | Balance: ₹{payBalance.toFixed(2)}</>}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Round Off — appears on the client invoice */}
            <div className="space-y-1.5 rounded-md bg-blue-50 border border-blue-100 p-3">
              <Label className="text-blue-800">Round Off (₹) — shown on invoice</Label>
              <Input
                type="number"
                placeholder="0"
                value={roundOffAmount}
                onChange={(e) => setRoundOffAmount(e.target.value)}
                min="0"
                max={payTotal}
              />
              <p className="text-[11px] text-blue-600">Small clean-up adjustment. Prints as a "Round Off" line on the client invoice and reduces amount owed.</p>
            </div>

            {/* Internal discount — not shown on client invoice */}
            <div className="space-y-1.5 rounded-md bg-orange-50 border border-orange-100 p-3">
              <Label className="text-orange-800">Discount (₹) — internal only</Label>
              <Input
                type="number"
                placeholder="0"
                value={discountAmount}
                onChange={(e) => { setDiscountAmount(e.target.value); setDiscountReasonSkipped(false); }}
                min="0"
                max={payTotal}
              />
              <p className="text-[11px] text-orange-600">Goodwill / negotiated discount. Reduces amount owed. NOT shown on the client invoice.</p>

              {/* Reason note — appears only when a discount is entered. It nudges the
                  accountant to record why; Skip records a plain discount with no reason. */}
              {payDisc > 0 && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-orange-800 text-sm">Reason for discount</Label>
                    {needDiscountReason && (
                      <button
                        type="button"
                        onClick={() => { setDiscountReason(''); setDiscountReasonSkipped(true); }}
                        className="text-[11px] text-gray-500 underline hover:text-gray-700"
                        data-testid="discount-reason-skip"
                      >
                        Skip
                      </button>
                    )}
                  </div>
                  <Textarea
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder="e.g. Repeat client, negotiated rate"
                    rows={2}
                    data-testid="discount-reason-input"
                  />
                  {discountReasonSkipped && !discountReason.trim() && (
                    <p className="text-[11px] text-gray-500">No reason recorded — saved as a regular discount.</p>
                  )}
                </div>
              )}
            </div>

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base"
              onClick={handleFullPayment}
              disabled={needDiscountReason}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Full Payment Received
              <span className="ml-1 text-sm opacity-80">
                (₹{payBalance.toFixed(2)})
              </span>
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-500">or</span></div>
            </div>
            <div className="space-y-2">
              <Label>Partial Payment Amount (₹)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  min="1"
                  max={payBalance}
                  className="flex-1"
                />
                <Button
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  onClick={handlePartialPayment}
                  disabled={!partialAmount || parseFloat(partialAmount) <= 0 || needDiscountReason}
                >
                  Record
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return to Coordinator dialog */}
      <Dialog open={!!returnEstimate} onOpenChange={(open) => { if (!open) { setReturnEstimate(null); setReturnReason(''); } }}>
        <DialogContent data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <CornerUpLeft className="w-5 h-5" /> Return to Coordinator
            </DialogTitle>
            <DialogDescription>
              This sends job card <strong>{returnEstimate?.job_card_number ? `#${returnEstimate.job_card_number}` : ''}</strong> ({returnEstimate?.customer_name}) back to the coordinator for changes. The draft estimate will be removed and regenerated when the card is approved again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="return-reason">Reason <span className="text-red-500">*</span></Label>
            <Textarea
              id="return-reason"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="e.g. Coordinator forgot to add edge-banding line"
              rows={3}
              data-testid="return-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReturnEstimate(null); setReturnReason(''); }}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleReturnToCoordinator}
              disabled={returning || !returnReason.trim()}
              data-testid="confirm-return-btn"
            >
              {returning ? 'Returning...' : 'Return to Coordinator'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User dialog — add-only. No user list, no edit/delete, no password reveal.
          Owner/Admin roles are never offered here and are blocked on the server. */}
      <Dialog open={showAddUser} onOpenChange={(open) => { if (!open) { setShowAddUser(false); setNewUser(emptyNewUser); } }}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Add User
            </DialogTitle>
            <DialogDescription>
              Create a login for a new team member. They can sign in with their mobile number and the password you set here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-user-name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="new-user-name"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-user-mobile">Mobile number <span className="text-red-500">*</span></Label>
              <Input
                id="new-user-mobile"
                value={newUser.mobile}
                onChange={(e) => setNewUser(prev => ({ ...prev, mobile: e.target.value }))}
                placeholder="e.g. 9995676674"
                data-testid="new-user-mobile"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-user-role">Role <span className="text-red-500">*</span></Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                <SelectTrigger id="new-user-role" data-testid="new-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ADDABLE_ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-user-password">Initial password <span className="text-red-500">*</span></Label>
              <Input
                id="new-user-password"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Set a password to share with them"
                data-testid="new-user-password"
              />
              <p className="text-[11px] text-gray-500">Share this with the new user so they can log in. They can keep using it.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddUser(false); setNewUser(emptyNewUser); }}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleAddUser}
              disabled={addingUser}
              data-testid="confirm-add-user"
            >
              {addingUser ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountantDashboard;
